import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Save, Trash2, Bell } from "lucide-react";

/** Platform alerts CRUD — banners & broadcasts to a specific audience. */
export default function CmsAlerts() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await http.get("/admin/cms/alerts");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load alerts");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    setBusy(true);
    try {
      await http.post("/admin/cms/alerts", payload);
      toast.success("Alert published");
      setShowNew(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async (a) => {
    try {
      await http.patch(`/admin/cms/alerts/${a.id}`, {
        title: a.title, body: a.body, severity: a.severity, audience: a.audience,
        starts_at: a.starts_at, ends_at: a.ends_at, active: a.active,
        cta_label: a.cta_label, cta_url: a.cta_url,
      });
      toast.success("Alert saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete alert?")) return;
    try {
      await http.delete(`/admin/cms/alerts/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-4xl" data-testid="cms-alerts">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Alerts</h1>
          <p className="mt-1 text-sm text-slate-500">Broadcast banners and notifications platform-wide or per-brand.</p>
        </div>
        <Button onClick={() => setShowNew((v) => !v)} data-testid="cms-alert-new-toggle" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="mr-1 h-4 w-4" /> {showNew ? "Cancel" : "New alert"}
        </Button>
      </div>

      {showNew && <AlertForm busy={busy} onSubmit={create} />}

      <div className="mt-4 space-y-3">
        {items.map((a) => (
          <AlertCard key={a.id} a={a} onSave={save} onRemove={remove} />
        ))}
        {items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No alerts yet — click "New alert" to broadcast one.
          </div>
        )}
      </div>
    </div>
  );
}

function AlertForm({ initial, busy, onSubmit, submitLabel }) {
  const [draft, setDraft] = useState(initial || emptyAlert());
  useEffect(() => { if (initial) setDraft(initial); }, [initial]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="cms-alert-form">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <FieldFull label="Title *" full><Input data-testid="cms-alert-title" value={draft.title} onChange={(e) => set("title", e.target.value)} /></FieldFull>
        <FieldFull label="Body" full><Textarea rows={3} value={draft.body} onChange={(e) => set("body", e.target.value)} /></FieldFull>
        <FieldFull label="Severity">
          <select value={draft.severity} onChange={(e) => set("severity", e.target.value)} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" data-testid="cms-alert-severity">
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="critical">critical</option>
          </select>
        </FieldFull>
        <FieldFull label="Audience">
          <select value={draft.audience} onChange={(e) => set("audience", e.target.value)} className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm">
            <option value="all">all</option>
            <option value="foodpulse">foodpulse</option>
            <option value="socialpulse">socialpulse</option>
            <option value="indianfamilytree">indianfamilytree</option>
            <option value="admins">admins</option>
          </select>
        </FieldFull>
        <FieldFull label="Starts at (ISO or blank)"><Input value={draft.starts_at || ""} onChange={(e) => set("starts_at", e.target.value)} /></FieldFull>
        <FieldFull label="Ends at (ISO or blank)"><Input value={draft.ends_at || ""} onChange={(e) => set("ends_at", e.target.value)} /></FieldFull>
        <FieldFull label="CTA label"><Input value={draft.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} /></FieldFull>
        <FieldFull label="CTA URL"><Input value={draft.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} /></FieldFull>
        <FieldFull label="Active" full>
          <div className="flex items-center gap-2">
            <Switch checked={!!draft.active} onCheckedChange={(v) => set("active", !!v)} data-testid="cms-alert-active" />
            <span className="text-xs text-slate-500">Toggle off to keep the alert without broadcasting.</span>
          </div>
        </FieldFull>
      </div>
      <div className="mt-2 flex justify-end">
        <Button onClick={() => onSubmit(draft)} disabled={busy || !draft.title.trim()} data-testid="cms-alert-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Save className="mr-1 h-4 w-4" /> {busy ? "Saving…" : (submitLabel || "Publish alert")}
        </Button>
      </div>
    </div>
  );
}

function AlertCard({ a, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-1">
        <AlertForm initial={a} onSubmit={(d) => { onSave(d); setEditing(false); }} submitLabel="Save changes" />
        <div className="px-4 pb-3">
          <button className="text-xs text-slate-500 hover:underline" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cms-alert-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Bell className={`h-4 w-4 ${a.severity === "critical" ? "text-rose-500" : a.severity === "warning" ? "text-amber-500" : "text-sky-500"}`} />
            <div className="text-sm font-semibold text-slate-900">{a.title}</div>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${a.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {a.active ? "active" : "paused"}
            </span>
          </div>
          {a.body && <p className="mt-1 text-xs text-slate-600">{a.body}</p>}
          <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
            <span>severity: {a.severity}</span>
            <span>·</span>
            <span>audience: {a.audience}</span>
            {a.starts_at && (<><span>·</span><span>from {a.starts_at.slice(0, 10)}</span></>)}
            {a.ends_at && (<><span>·</span><span>to {a.ends_at.slice(0, 10)}</span></>)}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button onClick={() => setEditing(true)} className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">Edit</button>
          <button onClick={() => onRemove(a.id)} className="rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>
    </div>
  );
}

function FieldFull({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="mb-1 block text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function emptyAlert() {
  return {
    title: "", body: "", severity: "info", audience: "all",
    starts_at: "", ends_at: "", active: true, cta_label: "", cta_url: "",
  };
}
